import { createCombinedAbortSignal } from './abortSignalUtils';
import streamFn from './streamClient';
import { deriveAuthFromDomain, getBffGateway } from './chirp-bff';
import { getGlobal } from './apiClients/global';
import { createStreamHandlerBase } from './streamHandlerBase';
import { getBffEarlyStreamEntry, getStreamingBffEarlyRequestName, removeBffEarlyStreamEntry } from './chirp-bff-streaming-quick-fetch';
const FIVE_MINUTES_MS = 5 * 60 * 1000;
export function createBffStreamHandler(args) {
  const {
    gatewayUrl,
    request,
    auth,
    portalId,
    userId,
    environment,
    hublet,
    timeout,
    calledFrom,
    rpcFetcherFactory,
    fetcherOverride,
    bffHash,
    name,
    earlyRequestKey
  } = args;
  const streamTimeout = typeof timeout === 'number' ? timeout : FIVE_MINUTES_MS;
  return createStreamHandlerBase({
    syncStackError: new Error(`BFF streaming call failed for ${bffHash}/${name}`),
    validate: () => {
      if (!fetcherOverride && !rpcFetcherFactory) {
        return new Error('Streaming BFFs require a fetch client (fetcherOverride or rpcFetcherFactory); hubHttpClient is not supported');
      }
    },
    run: async (signal, onConnected, onProgress, onError, onClose) => {
      const combinedSignal = createCombinedAbortSignal(streamTimeout, signal);
      const makeNormalFetch = () => {
        const fetcher = fetcherOverride !== null && fetcherOverride !== void 0 ? fetcherOverride : rpcFetcherFactory(auth, environment, hublet);
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream'
        };
        if (portalId !== undefined && (auth === 'internal' || calledFrom === 'npmShim')) {
          headers['X-Origin-HubId'] = String(portalId);
        }
        if (userId !== undefined) {
          headers['X-Origin-UserId'] = String(userId);
        }
        return fetcher(`${gatewayUrl}?clienttimeout=${streamTimeout}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
          signal: combinedSignal
        });
      };
      let response;
      const earlyEntry = earlyRequestKey ? getBffEarlyStreamEntry(earlyRequestKey) : undefined;
      if (earlyEntry) {
        // Consume-once: evict the entry before awaiting so a second concurrent
        // call cannot also consume it. Do NOT abort the early abortController —
        // cancellation is managed by our own combinedSignal.
        removeBffEarlyStreamEntry(earlyRequestKey);
        try {
          response = await earlyEntry.response;
          if (!response.ok) {
            var _response$body;
            // Early request returned a non-2xx status; discard its body and
            // fall back to a fresh fetch (matches chirp-avro-streaming.ts).
            (_response$body = response.body) === null || _response$body === void 0 || _response$body.cancel().catch(() => {});
            response = await makeNormalFetch();
          }
        } catch (_unused) {
          // Early fetch threw (e.g. network error before connect); fall back.
          response = await makeNormalFetch();
        }
      } else {
        response = await makeNormalFetch();
      }
      if (response.status === 401) {
        throw Object.assign(new Error('Unauthorized'), {
          isAuthError: true
        });
      }
      onConnected();
      await streamFn(response, {
        onProgress,
        onError,
        onClose,
        signal: combinedSignal
      });
    }
  });
}
export function createStreamingBffClient({
  fetcherOverride,
  rpcFetcherFactory,
  calledFrom
}) {
  return {
    call(details, inputs) {
      var _location$hostname, _location, _ref;
      const auth = deriveAuthFromDomain((_location$hostname = (_location = getGlobal().location) === null || _location === void 0 ? void 0 : _location.hostname) !== null && _location$hostname !== void 0 ? _location$hostname : '');
      const {
        timeout,
        portalId,
        userId,
        environment,
        hublet,
        args,
        earlyRequestName
      } = (_ref = inputs) !== null && _ref !== void 0 ? _ref : {};
      const request = {
        args: [args !== null && args !== void 0 ? args : {}]
      };
      const {
        bffHash,
        name
      } = details;
      const gatewayUrl = `${getBffGateway(auth)}/execute/hash/${bffHash}/${name}`;

      // Pre-compute the early-request key so createBffStreamHandler.run can
      // look up any in-flight early stream entry without needing details/inputs.
      const earlyRequestKey = earlyRequestName !== null && earlyRequestName !== void 0 ? earlyRequestName : getStreamingBffEarlyRequestName(details, inputs);
      return createBffStreamHandler({
        gatewayUrl,
        request,
        auth,
        portalId,
        userId,
        environment,
        hublet,
        timeout,
        calledFrom: calledFrom !== null && calledFrom !== void 0 ? calledFrom : 'bend',
        rpcFetcherFactory,
        fetcherOverride,
        bffHash,
        name,
        earlyRequestKey
      });
    }
  };
}